import React, { useEffect, useState, useContext } from "react";
import { useHistory } from "react-router-dom";
import { makeGETRequestOnUserService, makeGETRequestOnUserServiceAndDownloadFileResponse, makeDELETERequestOnUserServiceWithQueryParameter } from "../../Api/api";
import {
    Box,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
} from "@mui/material";
import { Grid } from "@material-ui/core";
import Paper from "@material-ui/core/Paper";
import { GridToolbar, DataGrid } from "@material-ui/data-grid";
import Skeleton from "@mui/material/Skeleton";
import { User } from "./DownloadPermissions";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DownloadIcon from "@mui/icons-material/Download";
import FullScreenDialog from "../../components/Dialog/FullScreenDialog";
import PartnerClientDetails from "../../components/PartnerClientDetails/PartnerClientDetails";
import PartnerClientEditDetails from "../../components/PartnerClientDetails/PartnerClientEditDetails";
import { PartnerSidebar } from "../../components/PartnerClientDetails/PartnerSidebar";
import { ClientFilterDialog } from "../../components/PartnerClientDetails/ClientFilterDialog";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import { appContext } from "../../App";
import Tooltip from "@material-ui/core/Tooltip";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";

interface UserWithClients extends User {
    clients?: any[];
}

interface MobileOption {
    uuid: string;
    mobile_number: string;
    company_name?: string;
    name: string;
    partner_name?: string;
}

export const downloadKYCDocs = (
    row: any,
    user_type: "partner" | "client",
    toastr: any
) => {
    makeGETRequestOnUserServiceAndDownloadFileResponse(
        `/partneruser/downloadzip/${row.uuid}/?user_type=${user_type}`
    )
        .then(({ blob, filename }) => {
            const downloadLink = document.createElement("a");
            const url = window.URL.createObjectURL(blob);

            downloadLink.href = url;
            downloadLink.download = filename || `${row.uuid}.zip`;

            document.body.appendChild(downloadLink);
            downloadLink.click();

            document.body.removeChild(downloadLink);
            window.URL.revokeObjectURL(url);
        })
        .catch((error) => {
            console.error(error);

            toastr.error(
                error?.message ||
                "An error occurred while downloading the ZIP file"
            );
        });
};


const PartnersUsersDashboard = () => {
    const toastr = require("toastr");
    const history = useHistory();
    const { authUser, setAuthUser } = useContext(appContext);
    const [users, setUsers] = useState<UserWithClients[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [selectedPartner, setSelectedPartner] = useState<any>(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [viewDialogOpen, setViewDialogOpen] = useState(false);
    const [selectedUserForView, setSelectedUserForView] = useState<any>(null);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [selectedUserForEdit, setSelectedUserForEdit] = useState<any>(null);
    const editDetailsRef = React.useRef<any>(null);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [selectedUserType, setSelectedUserType] = useState<string>("");
    const [selectedMobile, setSelectedMobile] = useState<string>("");
    const [isDeleting, setIsDeleting] = useState(false);

    const fetchUsers = () => {
        setIsLoading(true);
        makeGETRequestOnUserService("/partneruser/user_list/")
            .then((resp) => {
                if (resp.status === 200) setUsers(resp.data || []);
            })
            .catch((err) => console.error("Failed to fetch partners user list.", err))
            .finally(() => setIsLoading(false));
    };

    useEffect(() => { fetchUsers(); }, []);

    const partnerOptions: MobileOption[] = users.map((u) => ({
        uuid: u.uuid,
        mobile_number: (u as any).mobile_number,
        company_name: (u as any).company_name,
        name: `${(u as any).first_name || ""} ${(u as any).last_name || ""}`.trim(),
    }));

    const clientOptions: MobileOption[] = users.flatMap((partner) =>
        (partner.clients || []).map((c: any) => ({
            uuid: c.uuid,
            mobile_number: c.mobile_number,
            company_name: (partner as any).company_name,
            partner_name: `${(partner as any).first_name || ""} ${(partner as any).last_name || ""}`.trim(),
            name: `${c.first_name || ""} ${c.last_name || ""}`.trim(),
        }))
    );

    const mobileOptions: MobileOption[] =
        selectedUserType === "partner"
            ? partnerOptions
            : selectedUserType === "client"
                ? clientOptions
                : [];

    const handleDeleteUserInformation = async () => {
        if (!selectedUserType || !selectedMobile) {
            toastr.error("Please select user type and mobile number");
            return;
        }

        const selectedRecord = mobileOptions.find(
            (item) => item.mobile_number === selectedMobile
        );

        if (!selectedRecord) {
            toastr.error("User not found");
            return;
        }

        setIsDeleting(true);
        try {
            await makeDELETERequestOnUserServiceWithQueryParameter(
                `partneruser/user/destroy_all_information/${selectedRecord.uuid}/?user_type=${selectedUserType}`
            );

            toastr.success("User information deleted successfully");
            setOpenDeleteDialog(false);
            setSelectedUserType("");
            setSelectedMobile("");
            fetchUsers();
        } catch (error: any) {
            toastr.error(error?.message || "Something went wrong");
        } finally {
            setIsDeleting(false);
        }
    };

    const handleCloseDeleteDialog = () => {
        setOpenDeleteDialog(false);
        setSelectedUserType("");
        setSelectedMobile("");
    };


    const handleRowDoubleClick = (params: any) => {
        setSelectedPartner(params.row);
        setSidebarOpen(true);
    };

    const handleView = (row: any) => {
        setSelectedUserForView(row);
        setViewDialogOpen(true);
    };

    const handleEdit = (row: any) => {
        setSelectedUserForEdit(row);
        setEditDialogOpen(true);
    };

    const handleDownload = (row: any) => {
        downloadKYCDocs(row, "partner", toastr);
    };

    const handleEditUCC = (row: any) => {
        history.push(
            `/partner_client/update_ucc/${row.uuid}?user_type=partner`
        );
    };

    const dataGridCells = [
        { field: "serial", headerName: "Sr No", width: 120, renderCell: (p: any) => p.api.getRowIndex(p.id) + 1 },
        { field: "inserted_at", headerName: "Created At", width: 170, renderCell: (p: any) => p.value ? new Date(p.value).toLocaleString() : "-" },
        {
            field: "areKYCDetailsVerified", headerName: "KYC Status", width: 160,
        },
        { field: "company_name", headerName: "Company Name", width: 190, renderCell: (p: any) => `${p.row.company_name || ""}` },
        { field: "name", headerName: "Name", width: 150, renderCell: (p: any) => `${p.row.first_name || ""} ${p.row.last_name || ""}` },
        {
            field: "clients", headerName: "Clients", width: 130,
            renderCell: (p: any) => <span style={{ fontWeight: 600 }}>{(p.value || []).length}</span>,
        },
        { field: "email_id", headerName: "Email", width: 200 },
        { field: "mobile_number", headerName: "Mobile", width: 130 },
        { field: "date_of_birth", headerName: "DOB", width: 110, renderCell: (p: any) => p.value || "" },
        { field: "lead_owner", headerName: "Lead Owner", width: 160, renderCell: (p: any) => p.row?.leads_detail?.leadowner || "" },
        { field: "leadsstatus", headerName: "Leads Status", width: 170, renderCell: (p: any) => p.value || "" },
        { field: "isDeleted", headerName: "Deleted", width: 130, renderCell: (p: any) => (p.value ? "true" : "false") },
        { field: "smest_id", headerName: "SMEST ID", width: 150, renderCell: (p: any) => p.value || "" },
        {
            field: "view", headerName: "View", width: 90, sortable: false,
            renderCell: (p: any) => (
                <VisibilityIcon
                    style={{ cursor: "pointer", fontSize: 20 }}
                    onClick={(e) => {
                        e.stopPropagation();
                        handleView(p.row);
                    }}
                />
            ),
        },
        {
            field: "edit", headerName: "Edit", width: 90, sortable: false,
            renderCell: (p: any) => <EditIcon style={{ cursor: "pointer", fontSize: 20 }} onClick={(e) => { e.stopPropagation(); handleEdit(p.row); }} />,
        },
        {
            field: "download", headerName: "KYC Docs", width: 130, sortable: false,
            renderCell: (p: any) => <DownloadIcon style={{ cursor: "pointer", fontSize: 20 }} onClick={(e) => { e.stopPropagation(); handleDownload(p.row); }} />,
        },
        {
            field: "edit_ucc", headerName: "Edit UCC", width: 120, sortable: false,
            renderCell: (p: any) => <EditIcon style={{ cursor: "pointer", fontSize: 20, color: "red" }} onClick={(e) => { e.stopPropagation(); handleEditUCC(p.row); }} />,
        },
    ];

    return (
        <>
            <Grid className="QuotesDataGrid">
                <Paper>
                    <Grid style={{ margin: "20px 0", padding: "0 50px" }}>
                        <Grid container alignItems="center" spacing={3}>
                            <Grid item xs>
                                <div style={{ fontSize: "36px", float: "left" }}>Partners Users</div>
                            </Grid>
                            {authUser?.isSuperuser ||
                                JSON.parse(localStorage.getItem("permissions") || "[]").includes(
                                    "Partners Users"
                                ) ? (
                                <Grid item xs="auto">
                                    <Tooltip title="This will delete all user information permanently. Please be careful">
                                        <DeleteForeverIcon
                                            color="primary"
                                            fontSize="large"
                                            style={{
                                                float: "right",
                                                cursor: "pointer",
                                            }}
                                            onClick={() => setOpenDeleteDialog(true)}
                                        />
                                    </Tooltip>
                                </Grid>
                            ) : null}
                            <Grid item xs="auto">
                                <button
                                    onClick={() => setDialogOpen(true)}
                                    style={{
                                        background: isLoading ? "#ccc" : "#262349",
                                        cursor: isLoading ? "not-allowed" : "pointer",
                                        color: "white",
                                        border: "none",
                                        padding: "8px 16px",
                                        borderRadius: "4px",
                                        fontSize: "14px"
                                    }}
                                >
                                    {isLoading ? "Loading..." : "Filter Clients"}
                                </button>
                            </Grid>
                        </Grid>
                    </Grid>

                    <Box sx={{
                        height: "100vh", width: "100%", px: [2, 9],
                        "& .MuiDataGrid-columnHeaderTitle": { fontWeight: "bold" },
                        "& .MuiDataGrid-row": { cursor: "pointer" },
                        "& .MuiDataGrid-row:hover": { background: "#f5f7ff !important" },
                    }}>
                        {!isLoading ? (
                            <DataGrid
                                pagination
                                components={{ Toolbar: GridToolbar }}
                                rows={users}
                                columns={dataGridCells}
                                getRowId={(row) => row.uuid}
                                onRowDoubleClick={handleRowDoubleClick}
                            />
                        ) : (
                            <Box>{[1, 2, 3, 4, 5].map((i) => <Skeleton key={i} height={50} animation="wave" />)}</Box>
                        )}
                    </Box>
                </Paper>
            </Grid>

            <Dialog
                open={openDeleteDialog}
                onClose={handleCloseDeleteDialog}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Delete User Information</DialogTitle>

                <DialogContent>
                    {/* User Type Dropdown */}
                    <Box mt={2}>
                        <FormControl fullWidth>
                            <InputLabel id="user-type-label">User Type</InputLabel>
                            <Select
                                labelId="user-type-label"
                                value={selectedUserType}
                                label="User Type"
                                onChange={(e) => {
                                    setSelectedUserType(e.target.value as string);
                                    setSelectedMobile(""); // reset mobile when type changes
                                }}
                            >
                                <MenuItem value="partner">Partner</MenuItem>
                                <MenuItem value="client">Client</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>

                    {/* Mobile Number Dropdown — filtered by user type */}
                    <Box mt={2}>
                        <Autocomplete
                            disabled={!selectedUserType}
                            options={mobileOptions}
                            value={
                                mobileOptions.find(
                                    (item) => item.mobile_number === selectedMobile
                                ) || null
                            }
                            onChange={(_, value) => {
                                setSelectedMobile(value?.mobile_number || "");
                            }}
                            filterOptions={(options, { inputValue }) =>
                                options.filter((option) =>
                                    [
                                        option.mobile_number,
                                        option.name,
                                        option.company_name,
                                        option.partner_name,
                                    ]
                                        .filter(Boolean)
                                        .join(" ")
                                        .toLowerCase()
                                        .includes(inputValue.toLowerCase())
                                )
                            }
                            getOptionLabel={(option) => {
                                if (selectedUserType === "partner") {
                                    return `${option.mobile_number} - ${option.company_name || option.name
                                        }`;
                                }

                                return `${option.mobile_number} - ${option.name} - ${option.company_name || option.partner_name || ""
                                    }`;
                            }}
                            isOptionEqualToValue={(option, value) =>
                                option.uuid === value.uuid
                            }
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Mobile Number"
                                    placeholder="Search mobile number"
                                />
                            )}
                        />
                    </Box>

                    {/* Action Buttons */}
                    <Box
                        mt={3}
                        display="flex"
                        justifyContent="flex-end"
                        gap={2}
                    >
                        <Button
                            variant="outlined"
                            onClick={handleCloseDeleteDialog}
                            disabled={isDeleting}
                        >
                            Cancel
                        </Button>

                        <Button
                            variant="contained"
                            color="error"
                            onClick={handleDeleteUserInformation}
                            disabled={isDeleting || !selectedUserType || !selectedMobile}
                        >
                            {isDeleting ? "Deleting..." : "Delete"}
                        </Button>
                    </Box>
                </DialogContent>
            </Dialog>

            <PartnerSidebar
                open={sidebarOpen}
                partner={selectedPartner}
                onClose={() => setSidebarOpen(false)}
                fetchUsers={fetchUsers}
            />

            <ClientFilterDialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                clients={users.flatMap(partner => partner.clients || [])}
                partners={users}
                fetchUsers={fetchUsers}
            />

            <FullScreenDialog
                show={viewDialogOpen}
                heading={`${selectedUserForView?.first_name || ""} ${selectedUserForView?.last_name || ""}`}
                handleClose={() => setViewDialogOpen(false)}
                showSaveButton={true}
                saveButtonText="Save"
                onSumitSaveButton={() => {
                    setViewDialogOpen(false);
                    fetchUsers();
                }}
                inputProps={<PartnerClientDetails data={selectedUserForView} user_type="partner" />}
            />

            <FullScreenDialog
                show={editDialogOpen}
                heading="Partner Update Details"
                handleClose={() => setEditDialogOpen(false)}
                showSaveButton={true}
                saveButtonText="Save"
                onSumitSaveButton={async () => {
                    const success =
                        await editDetailsRef.current?.saveData();
                    if (success) {
                        setEditDialogOpen(false);
                        fetchUsers();
                    }
                }}
                inputProps={
                    <PartnerClientEditDetails
                        ref={editDetailsRef}
                        data={selectedUserForEdit}
                        user_type="partner"
                    />
                }
            />
        </>
    );
};

export default PartnersUsersDashboard;